import { useEffect, useState } from 'react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { showApiError } from '@/lib/utils/notifications'
import { getStockLogs } from '../services/stockRegistryService'
import type { Stock, StockLogsModalProps } from '../types'
import { formatStockAuditTimestamp } from '../utils/stockAuditFormat'

export function StockLogsModal({ isOpen, stockId, onClose }: StockLogsModalProps) {
  const [loading, setLoading] = useState(false)
  const [stock, setStock] = useState<Stock | null>(null)

  useEffect(() => {
    if (!isOpen || !stockId) return
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    getStockLogs(stockId)
      .then((data) => {
        if (!cancelled) setStock(data)
      })
      .catch((error) => {
        if (!cancelled) showApiError(error)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, stockId])

  const notes = stock?.notes

  return (
    <OnScreenModal isOpen={isOpen} title="Audit Logs" onClose={onClose} maxWidth="max-w-2xl">
      <div className="p-2 space-y-3">
        {loading ? (
          <ModuleScreenLoading variant="inline" className="py-6" data-testid="stock-logs-loading" />
        ) : !stock ? (
          <p className="text-sm text-slate-500 py-6 text-center">No data.</p>
        ) : notes && notes.length > 0 ? (
          <div className="overflow-x-auto rounded border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Changes By</th>
                  <th className="px-3 py-2 text-left font-semibold min-w-[200px]">Log</th>
                  <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">Created At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {notes.map((note, i) => (
                  <tr key={`${note.created_at ?? ''}-${i}`}>
                    <td className="px-3 py-2 text-slate-700 align-top">{note.added_by ?? '—'}</td>
                    <td
                      className="px-3 py-2 text-slate-700 align-top break-words min-w-[200px] max-w-md [&_a]:text-button [&_a]:underline"
                      dangerouslySetInnerHTML={{ __html: note.notes ?? '' }}
                    />
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap align-top">
                      {formatStockAuditTimestamp(note.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-500 py-4 text-center">
            No audit logs for this certificate.
          </p>
        )}

        <div className="flex justify-end pt-2 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </OnScreenModal>
  )
}
